// Angular imports
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// 3rd party imports
import { DxDataGridComponent, DxTreeListComponent } from 'devextreme-angular';
import CheckBox from 'devextreme/ui/check_box';
import * as events from 'devextreme/events';

// constant imports
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Service imports
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { AttachmentService } from '../../../_services/attachment.service';

// Model imports
import { DocumentParameterDto } from '../../../../shared-document-module/_models/document-parameter-dto';
import { DocumentResultDto } from '../../../../shared-document-module/_models/document-result-dto';
import { DocumentTreeResultDto } from '../../../../shared-document-module/_models/document-tree-result-dto';

@Component({
    selector: 'app-property-community-documents',
    templateUrl: './property-community-documents.component.html',
    styleUrls: ['./property-community-documents.component.scss']
})
export class PropertyCommunityDocumentsComponent {
    @ViewChild('documentTreeList', { static: false }) documentTreeList: DxTreeListComponent;
    @ViewChild('fileListDataGrid', { static: false }) fileListDataGrid: DxDataGridComponent;

    @Input() selectionMode: 'single' | 'multiple' = 'multiple';
    @Input() showSizeLimit = true;
    @Input() searchEnabled = true;
    @Input() allowAllExpandCollapse = true;
    @Input() allowedFileExtensions: Array<string>;
    @Output() onAttach: EventEmitter<DocumentResultDto[]> = new EventEmitter();

    visible = false;
    loadingVisible = false;
    menuRevealMode = 'slide';
    shaderEnabled = false;
    isDrawerOpen = true;
    initialSize = 0;
    maxAllowedSize = 26214400;
    maxSizeError = '';
    popupTitle = 'Attach Documents';
    noDataText = 'No documents found!';
    maxSizeErrorMessage = 'Attachments maximum size reached!';

    documentTree: DocumentTreeResultDto[] = [];
    documentsResult: DocumentResultDto[] = [];
    selectedKey: string[];
    currentNodeId: string;
    selectedNodeMap = {};
    selectedDocuments = [];
    selectedDocumentKeys = [];

    selectedAttachments: DocumentResultDto[];
    selectedSize: number;
    communityId: number;
    customerId: string;
    dateFormat: string = StringFormatConstants.dateFormat;

    searchText: string;
    searchButtonOptions: any;

    isEmptyDocumentTree = false;
    emptyDocumentsNote: string;

    iconClose = IconConstants.CLOSE;
    iconAttachment = IconConstants.ATTACHMENT;
    iconSearch = IconConstants.SEARCH;
    iconExpand = IconConstants.PLUS;
    iconCollapse = IconConstants.MINUS;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly attachmentService: AttachmentService
    ) {}

    show(
        title: string,
        communityId: number,
        customerId: string = null,
        selectedSize = 0,
        documents: Array<DocumentTreeResultDto> = []
    ): void {
        this.popupTitle = title;
        this.communityId = communityId;
        this.customerId = customerId;
        this.initialSize = selectedSize;
        this.selectedSize = selectedSize;
        this.selectedAttachments = [];
        this.noDataText = `No ${title.toLowerCase().replace('attach ', '')}s found!`;
        this.visible = true;

        if (!!documents && documents.length > 0) {
            this.processDocuments(documents);
        } else {
            this.populateDocumentsTree(communityId, customerId, true);
        }

        this.searchButtonOptions = {
            icon: this.iconSearch,
            stylingMode: 'text',
            onClick: () => {
                this.documentTreeList.instance.searchByText(this.searchText);
            }
        };
    }

    onShown(event): void {
        event.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
        this.selectedKey = null;
        this.documentTree = null;
        this.documentsResult = null;
        this.selectedNodeMap = {};
        this.selectedDocuments = [];
        this.selectedDocumentKeys = [];
        this.selectedSize = 0;
        this.initialSize = 0;
    }

    onDocumentTreeCellClick(event): void {
        this.documentsResult = null;
        if (Utils.notEqualsIgnoreCase(event.data.category, '')) {
            this.currentNodeId = event.data.id;
            const params = new DocumentParameterDto(
                event.data.customerId,
                event.data.communityId,
                event.data.category,
                event.data.subcategory,
                parseInt(event.data.month),
                parseInt(event.data.year)
            );
            this.populateDocumentsList(params);
            if (Utils.isNullOrUndefined(this.selectedNodeMap[this.currentNodeId])) {
                this.selectedNodeMap[this.currentNodeId] = [];
            }
            this.selectedDocumentKeys = [...this.selectedNodeMap[this.currentNodeId]];
        }
    }

    getFileIconByExtension(fileName: string): string {
        return this.helperService.getFileIconByExtension(fileName);
    }

    isFileSelectable(rec: DocumentResultDto): boolean {
        return (
            !this.allowedFileExtensions ||
            this.allowedFileExtensions.findIndex((ext) => rec.fileName.toLowerCase().endsWith(ext.toLowerCase())) !== -1
        );
    }

    formatFileSize(fileSize: number): string {
        return this.helperService.getFileSizeDisplayText(fileSize);
    }

    onDownloadFile(rec: DocumentResultDto): void {
        if (Utils.notNullOrEmpty(rec.fileUrl)) {
            this.loadingVisible = true;
            this.helperService.downloadFileUsingUrl(rec.fileUrl, rec.fileName, () => (this.loadingVisible = false));
        }
    }

    onFilesSelection(event: any): void {
        const filesize = Utils.equalsIgnoreCase(this.selectionMode, 'single') ? this.initialSize : this.selectedSize;
        let selectedSize = filesize;
        if (Utils.notNullOrEmpty(event.selectedRowsData)) {
            selectedSize = filesize + event.selectedRowsData.map((r) => r.size).reduce((c, t) => c + t);
        }
        this.selectedSize = 0;
        if (Utils.equalsIgnoreCase(this.selectionMode, 'multiple')) {
            if (this.maxAllowedSize > selectedSize) {
                this.selectedDocuments = this.selectedDocuments.concat(event.selectedRowsData);
                this.selectedNodeMap[this.currentNodeId] = [...event.selectedRowKeys];
                this.maxSizeError = '';
            } else {
                event.component.deselectRows(this.selectedDocumentKeys);
                this.maxSizeError = this.maxSizeErrorMessage;
            }
            this.selectedSize = this.calculateTotalSize();
        } else if (
            (this.maxAllowedSize > selectedSize &&
                (Utils.notEquals(this.selectedSize, selectedSize) ||
                    Utils.notEquals(this.selectedAttachments.length, event.selectedRowsData.length))) ||
            Utils.isFalse(this.showSizeLimit)
        ) {
            this.selectedAttachments = event.selectedRowsData;
            this.selectedSize = selectedSize;
            this.maxSizeError = '';
        } else {
            event.component.selectRows(this.selectedAttachments.map((r) => r.fileId));
            this.maxSizeError = this.maxSizeErrorMessage;
        }
    }

    onSingleAttachClick(rec: DocumentResultDto): void {
        if (this.maxAllowedSize > rec.size || this.showSizeLimit === false) {
            this.selectedAttachments = [rec];
            this.selectedSize = rec.size;
            this.maxSizeError = '';
            this.onAttachClick();
        } else {
            this.maxSizeError = this.maxSizeErrorMessage;
        }
    }

    onMultiAttachClick(selectedSize: number): void {
        if (selectedSize > this.maxAllowedSize) {
            this.maxSizeError = this.maxSizeErrorMessage;
        } else {
            this.selectedAttachments = [];
            this.maxSizeError = '';
            Object.keys(this.selectedNodeMap).forEach((key) => {
                const selectedDocKeys = this.selectedNodeMap[key];
                selectedDocKeys.forEach((fileId) => {
                    const file = this.selectedDocuments.find((doc) => doc.fileId === fileId);
                    this.selectedAttachments.push(file);
                });
            });

            this.onAttachClick();
        }
    }

    onAttachClick(): void {
        this.selectedAttachments.forEach((r) => {
            r.communityId = this.communityId;
            r.customerId = this.customerId;
        });
        this.onAttach.emit(this.selectedAttachments);
        this.visible = false;
    }

    onExpandClick(): void {
        this.documentTree.forEach((row) => {
            this.documentTreeList.instance.expandRow(row.id);
        });
    }

    onCollapseClick(): void {
        this.documentTree.forEach((row) => {
            if (row.parentId !== '0') {
                this.documentTreeList.instance.collapseRow(row.id);
            }
        });
    }

    onSearchTree(): void {
        this.documentTreeList.instance.searchByText(this.searchText);
    }

    onSearchInputChange(event: any): void {
        const searchText = event.event.currentTarget.value;
        this.documentTreeList.instance.searchByText(searchText);
    }

    onCellPrepared(e: any): void {
        if (Utils.equalsIgnoreCase(e.rowType, 'data') && Utils.equalsIgnoreCase(e.column.command, 'select')) {
            const checkboxElement = e.cellElement.querySelector('.dx-select-checkbox');
            const instance = CheckBox.getInstance(checkboxElement);
            const enableCheckBox = this.isFileSelectable(e.data);
            if (Utils.isFalse(enableCheckBox)) {
                instance.option('disabled', true);
                events.off(e.cellElement);
            }
        }
    }

    private populateDocumentsTree(communityId: number, customerId: string, clearCache = false): void {
        this.loadingVisible = true;
        this.attachmentService.getDocumentsTree(communityId, customerId, clearCache).subscribe(
            (results: DocumentTreeResultDto[]) => {
                this.processDocuments(results); // loading panel is hidden in processDocuments method
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private processDocuments(results: DocumentTreeResultDto[]): void {
        results.forEach((result) => {
            if (!(result.parentId === '0' && result.id === '0')) {
                result.parentId = result.parentId + 1;
            }
            result.id = result.id + 1;
        });

        const documentTree = results.filter((node) => {
            return !node.text.trim().endsWith('(0)');
        });

        if (Utils.notNullOrEmpty(documentTree)) {
            this.isEmptyDocumentTree = false;
            setTimeout(() => {
                this.documentTree = documentTree;
                this.loadingVisible = false;
            }, 500);
        } else {
            this.isEmptyDocumentTree = true;
            this.loadingVisible = false;
        }
    }

    private populateDocumentsList(params: DocumentParameterDto): void {
        this.loadingVisible = true;
        params.clearCache = true;
        this.attachmentService.getDocumentsList(params).subscribe(
            (results: DocumentResultDto[]) => {
                this.documentsResult = results;
                this.emptyDocumentsNote = Utils.notNullOrEmpty(this.documentsResult) ? '' : 'No data to display!';
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private calculateTotalSize(): number {
        let totalSize = 0;
        this.selectedDocuments = this.selectedDocuments.reduce((acc, current) => {
            const existing = acc.find((doc) => doc.fileId === current.fileId);
            if (!existing) {
                acc.push(current);
            }
            return acc;
        }, []);

        Object.keys(this.selectedNodeMap).forEach((key) => {
            const selectedDocKeys = this.selectedNodeMap[key];
            selectedDocKeys.forEach((fileId) => {
                const file = this.selectedDocuments.find((doc) => doc.fileId === fileId);
                if (file) {
                    totalSize += file.size;
                }
            });
        });

        return Utils.equals(this.initialSize, 0) ? totalSize : totalSize + this.initialSize;
    }
}
